// FILE: StudentUploadUI.js
import React, { useState } from "react";
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions
    , Typography, Paper, Select, MenuItem,
    FormControl, Table, TableHead, TableRow, TableCell,
    TableBody, IconButton, TextField, CircularProgress,
    Grid, useTheme, useMediaQuery, Avatar, Alert, Collapse
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SchoolIcon from "@mui/icons-material/School";
import GroupIcon from "@mui/icons-material/Group";
import SettingsIcon from "@mui/icons-material/Settings";

import api from "../axios/axiosInstance";
import { SpinningGradelyLoader, FloatingGradelyLoader } from "../loadingIcons/LoadingIcons";

const academicYears = ["2025-2026",
    "2026-2027",
    "2027-2028",
    "2028-2029",
    "2029-2030",];
const sectionOptions = ["1", "2", "3", "4", "5", "7", "8", "9"];
const classOptions = ["KG", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
const pronouns = ["He", "She", "They"];


const StudentUploadUI = () => {
    const theme = useTheme();

    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const [globalClassLevel, setGlobalClassLevel] = useState("");
    const [globalSectionLevel, setGlobalSectionLevel] = useState("");
    const [globalAcademicYear, setGlobalAcademicYear] = useState("");
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showTable, setShowTable] = useState(true);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [addStudentDialogOpen, setAddStudentDialogOpen] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewMissingFields, setViewMissingFields] = useState([]);


    const fetchStudents = async () => {
        const missing = [];
        if (!globalClassLevel) missing.push("Class Level");
        if (!globalSectionLevel) missing.push("Section");
        if (!globalAcademicYear) missing.push("Academic Year");

        if (missing.length > 0) {
            setViewMissingFields(missing);
            setViewDialogOpen(true);
            return;
        }

        setLoading(true);

        try {
            const res = await api.get(`/auth/students/`);
            const data = res.data;

            const filtered = data.filter(
                (s) =>
                    s.class_level == globalClassLevel &&
                    s.section_level == globalSectionLevel &&
                    s.academic_year == globalAcademicYear
            );
            const withEditFlag = filtered.map(s => ({ ...s, isEdited: false }));
            const sorted = withEditFlag.sort((a, b) => a.last_name.localeCompare(b.last_name));
            setStudents(sorted);
        } catch (err) {
            // Error handled silently
        } finally {
            setLoading(false);
        }
    };

    const handleAddStudent = () => {
        const missing = [];
        if (!globalClassLevel) missing.push("Class Level");
        if (!globalSectionLevel) missing.push("Section");
        if (!globalAcademicYear) missing.push("Academic Year");

        if (missing.length > 0) {
            setMissingFields(missing);
            setAddStudentDialogOpen(true);
            return;
        }

        const newStudent = {
            id: Date.now(),
            first_name: "",
            middle_name: "",
            last_name: "",
            pronoun: "They",
            class_level: globalClassLevel,
            section_level: globalSectionLevel,
            academic_year: globalAcademicYear,
            study_ethics: "",
            isNew: true
        };
        setStudents((prev) => [newStudent, ...prev]);
    };

    const handleChange = (id, field, value) => {
        setStudents((prev) =>
            prev.map((s) =>
                s.id === id ? { ...s, [field]: value, isEdited: !s.isNew ? true : s.isEdited } : s
            )
        );
    };

    const handleDelete = async (student) => {
        if (student.isNew) {
            setStudents((prev) => prev.filter((s) => s.id !== student.id));
            return;
        }

        try {
            await api.delete(`/auth/students/${student.id}/`);
            setStudents((prev) => prev.filter((s) => s.id !== student.id));
        } catch (err) {
            // Error handled silently
        }
    };

    const handleSave = async () => {
        const newStudents = students.filter((s) => s.isNew);
        const updatedStudents = students.filter((s) => s.isEdited && !s.isNew);

        const invalidStudents = [...newStudents, ...updatedStudents].filter(
            (s) => !s.first_name.trim() || !s.last_name.trim()
        );

        if (invalidStudents.length > 0) {
            const issues = invalidStudents.map((s, i) => {
                const fields = [];
                if (!s.first_name.trim()) fields.push("First Name");
                if (!s.last_name.trim()) fields.push("Last Name");
                return `Student ${i + 1}: Missing ${fields.join(" and ")}`;
            });

            setValidationErrors(issues);
            setValidationDialogOpen(true);
            return;
        }

        if (newStudents.length === 0 && updatedStudents.length === 0) {
            setValidationErrors(["Nothing to save."]);
            setValidationDialogOpen(true);
            return;
        }

        setSaving(true);

        try {
            if (newStudents.length > 0) {
                await api.post(`/auth/students/upload/`, newStudents);
            }

            if (updatedStudents.length > 0) {
                await api.patch(`/auth/students/update/`, { updates: updatedStudents });
            }

            fetchStudents();
        } catch (err) {
            // Error handled silently
        } finally {
            setSaving(false);
        }
    };

    const getUnsavedCount = () => students.filter(s => s.isNew || s.isEdited).length;

    return (
        <Box sx={{ p: 3, borderRadius: 4, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
            {/* Header */}
            <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}><GroupIcon /></Avatar>
                    <Box>

                        <Typography variant="body2" color="text.secondary" sx={{
                            color: theme.palette.primary.dark,
                            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.35rem', lg: '1.45rem' },
                            textAlign: "left",
                            lineHeight: 1.3
                        }}>Select Class Level, Section and Academic Year to view or add student(s)</Typography>
                    </Box>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}><StatCard label="Total Students" value={students.length} color="primary" /></Grid>
                    <Grid item xs={6} sm={3}><StatCard label="New Students" value={students.filter(s => s.isNew).length} color="success" /></Grid>
                    <Grid item xs={6} sm={3}><StatCard label="Modified" value={students.filter(s => s.isEdited).length} color="warning" /></Grid>
                    <Grid item xs={6} sm={3}><StatCard label="Unsaved Changes" value={getUnsavedCount()} color="info" /></Grid>
                </Grid>
            </Paper>

            {/* Filters */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>


                <Grid container spacing={2} mb={2}>
                    {[["Class Level", globalClassLevel, setGlobalClassLevel, classOptions],
                    ["Section", globalSectionLevel, setGlobalSectionLevel, sectionOptions],
                    ["Academic Year", globalAcademicYear, setGlobalAcademicYear, academicYears]].map(
                        ([label, value, setter, options]) => (
                            <Grid item xs={12} sm={4} key={label}>
                                <FormControl fullWidth size="small">
                                    <Select 
                                        value={value} 
                                        onChange={(e) => setter(e.target.value)} 
                                        displayEmpty
                                    >
                                        <MenuItem value="">{label}</MenuItem>
                                        {options.map((opt) => (
                                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )
                    )}
                </Grid>

                {/* Actions */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Button 
                            fullWidth 
                            variant="contained" 
                            color="primary" 
                            onClick={fetchStudents}
                            startIcon={loading ? <CircularProgress size={16} /> : <VisibilityIcon />}
                            disabled={loading}
                        >
                            {loading ? "Loading..." : "View Student List"}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button 
                            fullWidth 
                            variant="outlined" 
                            onClick={handleAddStudent} 
                            startIcon={<PersonAddAlt1Icon />}
                        >
                            Add Student
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button 
                            fullWidth 
                            variant="contained" 
                            color="success" 
                            onClick={handleSave}
                            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                            disabled={saving || getUnsavedCount() === 0}
                        >
                            {saving ? "Saving..." : "Save Changes"}
                        </Button>
                    </Grid>
                </Grid>

                {getUnsavedCount() > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                        You have {getUnsavedCount()} unsaved change(s). Click "Save Changes".
                    </Alert>
                )}
            </Paper>

            {/* Students Table */}
            {students.length > 0 && (
                <Paper elevation={3} sx={{ borderRadius: 3 }}>
                    <Box sx={{
                        p: 2,
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <GroupIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">Student List</Typography>
                        </Box>
                        <IconButton onClick={() => setShowTable(prev => !prev)} sx={{ color: "inherit" }}>
                            {showTable ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                    <Collapse in={showTable}>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size={isTablet ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: theme.palette.secondary.main }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>First Name</TableCell>
                                        <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>Middle Name</TableCell>
                                        <TableCell>Last Name</TableCell>
                                        <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>Class</TableCell>
                                        <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>Section</TableCell>
                                        <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>Pronoun</TableCell>
                                        <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>Academic Year</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {students.map((s, index) => (
                                        <TableRow key={s.id}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell><TextField variant="standard" value={s.first_name} onChange={(e) => handleChange(s.id, "first_name", e.target.value)} /></TableCell>
                                            <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}><TextField variant="standard" value={s.middle_name} onChange={(e) => handleChange(s.id, "middle_name", e.target.value)} /></TableCell>
                                            <TableCell><TextField variant="standard" value={s.last_name} onChange={(e) => handleChange(s.id, "last_name", e.target.value)} /></TableCell>
                                            <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}><TextField variant="standard" value={s.class_level} disabled /></TableCell>
                                            <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}><TextField variant="standard" value={s.section_level} disabled /></TableCell>
                                            <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                                                <Select value={s.pronoun} onChange={(e) => handleChange(s.id, "pronoun", e.target.value)} variant="standard">
                                                    {pronouns.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                                </Select>
                                            </TableCell>
                                            <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}><TextField variant="standard" value={s.academic_year} disabled /></TableCell>
                                            <TableCell>
                                                <IconButton onClick={() => handleDelete(s)}><DeleteIcon color="error" /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </Paper>
            )}
            {/* Validation Dialog for Save Button */}
            <Dialog open={validationDialogOpen} onClose={() => setValidationDialogOpen(false)}>
                <DialogTitle sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    ⚠️ Sorry some information is missing
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Please Enter The following {validationErrors.length > 1 ? "s" : ""}:
                    </Typography>
                    <ul>
                        {validationErrors.map((err, idx) => (
                            <li key={idx}>
                                <Typography variant="body2" color="text.secondary">
                                    {err}
                                </Typography>
                            </li>
                        ))}
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setValidationDialogOpen(false)} color="primary" variant="contained">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Add Student Dialog for missing class/section/year */}
            <Dialog open={addStudentDialogOpen} onClose={() => setAddStudentDialogOpen(false)}>
                <DialogTitle sx={{ color: "error.main", fontWeight: "bold" }}>
                    ⚠️ Missing Required Fields
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" gutterBottom>
                        Please select the following before adding a student:
                    </Typography>
                    <ul style={{ paddingLeft: 16 }}>
                        {missingFields.map((field, idx) => (
                            <li key={idx}>
                                <Typography variant="body2" color="text.secondary">{field}</Typography>
                            </li>
                        ))}
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddStudentDialogOpen(false)} variant="contained" color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
            {/* View Student List Dialog for missing class/section/year */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)}>
                <DialogTitle sx={{ color: "error.main", fontWeight: "bold" }}>
                    ⚠️ Missing Required Fields
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" gutterBottom>
                        Please select the following before viewing the student list:
                    </Typography>
                    <ul style={{ paddingLeft: 16 }}>
                        {viewMissingFields.map((field, idx) => (
                            <li key={idx}>
                                <Typography variant="body2" color="text.secondary">{field}</Typography>
                            </li>
                        ))}
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)} variant="contained" color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Loading State for Fetching Students */}
            {loading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Loading student data from database..."
                        />
                    </Paper>
                </Box>
            )}

            {/* Loading State for Saving Changes */}
            {saving && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Paper
                        elevation={8}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Saving student information changes to database..."
                        />
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

const StatCard = ({ label, value, color }) => (
    <Box sx={{ textAlign: 'center', p: 1 }}>
        <Typography variant="h6" fontWeight="bold" color={`${color}.main`}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
);

export default StudentUploadUI;